import React, { useContext, useState } from 'react';
import { Box, Text, Flex, Menu, Stack, Divider, Select, TextInput, Button, Group, Alert, ActionIcon } from '@mantine/core';
import { IconCaretDown, IconPlus, IconCheck, IconX } from '@tabler/icons';
import { showNotification } from '@mantine/notifications';
import IdeaContext from '../../IdeaContext';
import PerspectiveList from './PerspectiveList';
import SyncContext from "../../../../context/SyncContext";
import DiscoveryContext from '../../../Discovery/DiscoveryContext';
import { IdeaPerspective } from '@bageltechnologies/bagel-common';
import {
  useAvailablePerspectives,
  useUpdatePerspectives,
  usePerspectives,
} from '../../../../hooks/useCustomizations';

interface EnhancedBusinessSourcesProps {
  ideaPage: boolean;
  customizationName?: string;
}

const EnhancedBusinessSources: React.FC<EnhancedBusinessSourcesProps> = ({ 
  ideaPage, 
  customizationName = 'idea'
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedAvailablePerspective, setSelectedAvailablePerspective] = useState<string>('');
  const [perspectiveLabel, setPerspectiveLabel] = useState<string>('');
  const [perspectiveKey, setPerspectiveKey] = useState<string>('');

  const ContextToUse: React.Context<any> = 
    ideaPage ? IdeaContext : DiscoveryContext;

  const {
    type,
    setType
  } = useContext(ContextToUse);

  // Hooks for API calls
  const { data: availablePerspectives, isLoading: loadingAvailable } = useAvailablePerspectives();
  const { data: currentPerspectives } = usePerspectives();
  const updatePerspectives = useUpdatePerspectives();

  // Extract perspectives for the dropdown
  const perspectiveConfigurations = currentPerspectives?.data || [];

  // Transform available perspectives for Select component
  let perspectivesArray = [];
  if (availablePerspectives?.data && Array.isArray(availablePerspectives.data)) {
    perspectivesArray = availablePerspectives.data;
  } else if (availablePerspectives && Array.isArray(availablePerspectives)) {
    perspectivesArray = availablePerspectives;
  } else if (availablePerspectives?.success && availablePerspectives?.data && Array.isArray(availablePerspectives.data)) {
    perspectivesArray = availablePerspectives.data;
  }

  const availableOptions: { value: string; label: string }[] = perspectivesArray.map((perspective: any) => ({
    value: perspective.key,
    label: perspective.label,
  })) || [];

  // Check if perspective key already exists
  const keyExists = currentPerspectives?.data?.perspectives?.some(
    (p: IdeaPerspective) => p.key === perspectiveKey
  );

  const handleAvailablePerspectiveChange = (value: string | null) => {
    setSelectedAvailablePerspective(value || '');
    if (value) {
      const selectedOption = availableOptions.find((option) => option.value === value);
      if (selectedOption) {
        setPerspectiveLabel(selectedOption.label);
        setPerspectiveKey(value);
      }
    }
  };

  const handleSave = async () => {
    if (!perspectiveLabel.trim()) {
      showNotification({
        title: 'Validation Error',
        message: 'Please enter a perspective label',
        color: 'red',
      });
      return;
    }

    // Auto-generate key if not set
    const finalKey = perspectiveKey || perspectiveLabel.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    if (currentPerspectives?.data?.perspectives?.some((p: IdeaPerspective) => p.key === finalKey)) {
      showNotification({
        title: 'Duplicate Key',
        message: 'A perspective with this key already exists',
        color: 'red',
      });
      return;
    }

    try {
      const newPerspective: IdeaPerspective = {
        label: perspectiveLabel.trim(),
        key: finalKey,
        countType: 'requests',
      };

      const existingPerspectives = currentPerspectives?.data || [];
      const updatedPerspectives = [...existingPerspectives, newPerspective];

      await updatePerspectives.mutateAsync({
        perspectives: updatedPerspectives,
      });

      showNotification({
        title: 'Success',
        message: 'Perspective added successfully',
        color: 'green',
      });

      // Automatically select the newly added perspective
      setType(newPerspective.key);
      
      // Reset form
      handleCancel();
      
    } catch (error) {
      console.error('Error adding perspective:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to add perspective',
        color: 'red',
      });
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setSelectedAvailablePerspective('');
    setPerspectiveLabel('');
    setPerspectiveKey('');
  };

  const handleAddNewClick = () => {
    setShowAddForm(true);
  };

  return (
    <Stack pl={38} pr={85.4} align='stretch'>
      <Box mb={ideaPage ? 16 : 0} mt={26} py={2}>
        <Flex>
          <Text fz="14px" fw={600} mr='4px'>
            Perspective:
          </Text>
        
          <Menu 
            position='bottom' 
            withinPortal
            width={260}
            closeOnClickOutside={!showAddForm}
            closeOnItemClick={!showAddForm}
            withArrow
            shadow='md'
          >
            <Menu.Target>
              <Flex sx={{cursor: 'pointer'}}>
                <Text fz="14px" mr='4px'>
                  {perspectiveConfigurations?.find((i: any) => i.key == type)?.label}
                </Text>
                <IconCaretDown fill='black' size={14}/>
              </Flex>
            </Menu.Target>
            <Menu.Dropdown>
              {perspectiveConfigurations?.map((perspective: any) => (
                <Menu.Item
                  key={perspective.key}
                  onClick={() => {
                    setType(perspective.key);
                  }}
                >
                  {perspective.label}
                </Menu.Item>
              ))}
              
              {/* {!showAddForm && (
                <Box>
                  <Button
                    variant='subtle'
                    size='xs'
                    leftIcon={<IconPlus size={14} />}
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddNewClick();
                    }}
                  >
                      Add New
                  </Button>
                </Box>
              )}

              {showAddForm && (
                <Group spacing={0} position='apart' mt={4}>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    onClick={handleCancel}
                    disabled={updatePerspectives.isPending}
                  >
                    <IconX size={14} />
                  </ActionIcon>

                  <Select
                    placeholder="Select base"
                    data={availableOptions}
                    value={selectedAvailablePerspective}
                    onChange={handleAvailablePerspectiveChange}
                    disabled={loadingAvailable || updatePerspectives.isPending}
                    searchable
                    clearable
                    size="xs"
                    style={{ width: 100 }}
                  />

                  <TextInput
                    placeholder="Label"
                    value={perspectiveLabel}
                    onChange={(e) => {
                      setPerspectiveLabel(e.target.value);
                      // Auto-generate key from label
                      setPerspectiveKey(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''));
                    }}
                    disabled={updatePerspectives.isPending}
                    size="xs"
                    style={{ width: 100 }}
                  />

                  <ActionIcon
                    size="sm"
                    variant="filled"
                    onClick={handleSave}
                    loading={updatePerspectives.isPending}
                    disabled={
                      !perspectiveLabel.trim()
                    }
                  >
                    <IconCheck size={14} />
                  </ActionIcon>
                </Group>
              )} */}
            </Menu.Dropdown>
          </Menu>  
        </Flex>   
      </Box>
      <PerspectiveList ideaPage={ideaPage}/>
    </Stack>
  );
};

export default EnhancedBusinessSources; 